import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseUrl } from '../config/Global';
import { storeData, getData } from '../utils/storage';
import { showToast } from '../components/Toast';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginHide, setLoginHide] = useState(true);
  const [wait, setWait] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  useEffect(() => {
    const checkLogin = async () => {
      const mydata = getData('data');
      const login = getData('login');

      if (login === 'User' && mydata) {
        navigate('/package', { state: { data: mydata } });
      }
    };

    checkLogin();
  }, [navigate]);

  const submitLogin = async () => {
  setWait(true);

  if (!email) {
    showToast('Must enter email!', 'error');
    setWait(false);
    return;
  }

  if (!loginPassword) {
    showToast('Must enter password!', 'error');
    setWait(false);
    return;
  }

  try {
    // Step 1: Get user data (includes hashed password)
    const loginRes = await axios.post(`${baseUrl}/loginClient`, {
      email,
      password: loginPassword,
    });

    const user = loginRes.data?.data;

    if (!user?.password) {
      showToast('User not found or password missing!', 'error');
      setWait(false);
      return;
    }

    // Step 2: Verify password using correct form-encoded bcrypt API call
    const form = new URLSearchParams();
    form.append('plain_text', loginPassword.toString());
    form.append('hash', user.password.toString());

    const verifyRes = await axios.post('https://bcrypt.online/verify', form, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      maxBodyLength: Infinity,
    });

    const isVerified = verifyRes.data?.is_verified;

    if (!isVerified) {
      showToast('Password is incorrect!', 'error');
      setWait(false);
      return;
    }

    // Step 3: Get full user data
    const userDetailsRes = await axios.get(`${baseUrl}/company/getUser/${user.id}`);
    const userDetails = userDetailsRes.data?.data;

    // Step 4: Get access token
    const tokenRes = await axios.get(`${baseUrl}/token/${userDetails.id}`);
    const tokenData = tokenRes.data?.data;

    if (tokenRes.data.status === 'ok') {
      // Update access_token in DB if needed
      if (!user.access_token || user.access_token === 'null' || user.access_token === '') {
        await axios.put(`${baseUrl}/users/update/${user.id}`, {
          access_token: tokenData.value,
        });
      }

      showToast('Login Successfully!', 'success');
      setLoginPassword('');
      await storeData(user, tokenData.value, 'User');

      navigate('/package', { state: { data: tokenData } });
    } else {
      showToast(tokenRes.data.message || 'Token error', 'error');
    }
  } catch (err) {
    console.error(err);
    showToast('Login failed. Please try again.', 'error');
  }

  setWait(false);
};



  const handleResetPassword = () => {
    if (!resetEmail) {
      showToast('Must enter an email!', 'error');
      return;
    }
    
    setShowResetModal(false);
    setResetEmail('');
    navigate('/otp-verify', { state: { email: resetEmail } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-[#a8e82f] flex items-center justify-center p-4">
      {wait && <Loader />}
      
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden ">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#a8e82f] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gray-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow"></div>
        <div className="absolute top-40 left-1/2 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl p-8 animate-fade-in border border-white/20">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#a8e82f] rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Please enter your credentials to continue</p>
          </div>

          {/* Login Illustration */}
          <div className="text-center mb-8">
            <div className="w-32 h-32 mx-auto bg-gradient-to-r from-gray-100 to-lime-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-16 h-16 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Email Input */}
            <div className="space-y-2">
              {/* <label className="text-sm font-semibold text-gray-700">Email Address</label> */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-200 text-gray-800"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              {/* <label className="text-sm font-semibold text-gray-700">Password</label> */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type={loginHide ? "password" : "text"}
                  className="w-full pl-12 pr-12 py-4 bg-gray-50/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-200 text-gray-800"
                  placeholder="Enter your password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center mt-4"
                  onClick={() => setLoginHide(!loginHide)}
                >
                  {loginHide ? (
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            {/* <div className="text-right">
              <button
                type="button"
                className="text-sm text-gray-600 hover:text-gray-800 font-semibold transition-colors"
                onClick={() => setShowResetModal(true)}
              >
                Forgot Password?
              </button>
            </div> */}

            {/* Sign In Button */}
            <button
              type="button"
              className="w-full bg-[#a8e82f] text-black py-4 px-6 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={submitLogin}
              disabled={wait}
            >
              {wait ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing In...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      <Modal isVisible={showResetModal} onClose={() => setShowResetModal(false)}>
        <div className="p-8">
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={() => setShowResetModal(false)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Modal Content */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Reset Password</h3>
            <p className="text-gray-600">An email with the reset link will be sent to you</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email:</label>
              <input
                type="email"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>

            <div className="flex justify-end pt-4">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center space-x-2 transition-all duration-200 transform hover:-translate-y-0.5"
                onClick={handleResetPassword}
              >
                <span>Reset</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Login;